import { api } from "../api.js";
import { t, translatePage } from "../i18n.js";
import { icon } from "../icon.js";
import { preloadTripMap } from "../components/map-view.js";
import { renderLocationsTab } from "./locations-tab.js";
import { renderItineraryTab } from "./itinerary-tab.js";
import { renderNotesTab } from "./notes-tab.js";
import { renderFileList } from "../components/file-list.js";
import { renderChecklistList } from "../components/checklist-list.js";
import { renderSettingsTab } from "./settings-tab.js";
import { renderMembersTab } from "./members-tab.js";
import { renderExpensesTab } from "./expenses-tab.js";
import { canEdit, canManageMembers, isShared, isViewer } from "../trip-role.js";
import { TRIP_TABS, OVERFLOW_TRIP_TABS } from "../trip-tabs.js";
import { renderMenu } from "../components/menu.js";
import { navigate } from "../router.js";
import { formatDateRange } from "../format.js";
import { renderLoading } from "../components/loading.js";
import { renderNotFoundPage } from "./not-found-page.js";

const TABS = TRIP_TABS.map(({ key }) => key);

export async function renderTripDetailPage(container, { tripId, tab }, signal) {
  renderLoading(container, { size: "lg" });
  if (tab === "map") preloadTripMap(tripId);

  // Locations (not Overview) is what users actually want to land on - see
  // Stage 02 review. "/trips/:tripId" has no tab segment, so it
  // canonicalizes itself to the real tab URL rather than leaving a
  // tab-less URL in the address bar and history. Before the fetch, not after:
  // by the time a slow fetch returns the user may be on another entry, and
  // this would rewrite *that* one.
  if (!tab || !TABS.includes(tab)) {
    tab = "locations";
    // Spread rather than {}: this entry is the same stop on the way back, so
    // whatever router.js recorded about where it came from still holds.
    window.history.replaceState({ ...window.history.state }, "", `/trips/${tripId}/locations`);
  }

  let trip;
  try {
    trip = await api.get(`/trips/${tripId}`);
  } catch {
    renderNotFoundPage(container, { href: "/trips", labelKey: "common.home" });
    return;
  }

  // Switching tabs is a re-render of this page, not a route, so the router's
  // signal outlives it. Each tab gets its own, aborted by the next tab and by
  // the router's.
  let tabController = null;

  function render() {
    tabController?.abort();
    tabController = new AbortController();
    const tabSignal = AbortSignal.any([signal, tabController.signal]);
    // Subtitle and date range share one line, joined by a "·" that only
    // appears between two pieces that both actually exist. The range is
    // plain text rather than a labeled dt/dd pair - it reads fine as prose
    // but not as a table row.
    const dateRange = formatDateRange(trip.start_date, trip.end_date);
    const summaryParts = [];
    if (trip.subtitle) summaryParts.push(`<span class="trip-summary__subtitle"></span>`);
    if (dateRange) {
      if (summaryParts.length) summaryParts.push(`<span class="trip-summary__dot" aria-hidden="true">·</span>`);
      summaryParts.push(`<span class="trip-summary__dates">${dateRange}</span>`);
    }

    container.innerHTML = `
      <div class="page trip-detail">
        <a href="/trips" data-link class="back-link">${icon("arrow-left")} <span data-i18n="common.home"></span></a>
        ${trip.preview_image_url ? `<img class="trip-detail__cover" alt="" />` : ""}
        <div class="page__header">
          <h1></h1>
          ${isViewer(trip) ? `<span class="trip-detail__role" data-i18n="trip.viewerBadge"></span>` : ""}
        </div>
        ${summaryParts.length ? `<div class="trip-summary">${summaryParts.join("")}</div>` : ""}
        <nav class="trip-tabs">
          ${TRIP_TABS.map(
            ({ key, icon: tabIcon, overflow }) =>
              `<a href="/trips/${trip.id}/${key}" data-tab="${key}" class="${[key === tab ? "active" : "", overflow ? "trip-tabs__overflow-tab" : ""].filter(Boolean).join(" ")}"${key === tab ? ` aria-current="page"` : ""}>${icon(tabIcon)} <span data-i18n="trip.tabs.${key}"></span></a>`
          ).join("")}
          <div class="trip-tabs__more-slot"></div>
        </nav>
        <div class="trip-tab-content"></div>
      </div>
    `;
    translatePage(container);
    container.querySelector(".page__header h1").textContent = trip.title;
    const subtitleEl = container.querySelector(".trip-summary__subtitle");
    if (subtitleEl) subtitleEl.textContent = trip.subtitle;
    // src assigned as a property rather than interpolated into the template
    // above, the same way the title and subtitle are - no local escape helper
    // needed, and the URL can't break out of an attribute it never enters.
    // alt="" because it's decorative: the <h1> right below already names the
    // trip, so announcing the photo would just repeat it.
    const coverEl = container.querySelector(".trip-detail__cover");
    if (coverEl) coverEl.src = trip.preview_image_url;

    // Each tab is a real <a href> (Stage 47), so middle-click, Ctrl-click and
    // "Open link in new tab" work on it. No data-link, though: the router would
    // re-run the whole route and re-fetch the trip, so a plain left-click is
    // taken over here instead, and anything else is left to the browser - the
    // same guard as router.js and map-view.js.
    container.querySelectorAll("[data-tab]").forEach((link) => {
      link.addEventListener("click", (e) => {
        if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
        e.preventDefault();
        tab = link.getAttribute("data-tab");
        // Pushed directly (not via router.js's navigate) so switching tabs
        // stays a local re-render instead of re-fetching the trip through a
        // full route match - this only updates history/the URL bar. Back/
        // forward still works: browser back dispatches "popstate", which
        // the app-level router does listen for, and it re-renders this
        // page fresh from the URL at that point.
        window.history.pushState({}, "", `/trips/${trip.id}/${tab}`);
        render();
      });
    });

    // "More" holds the tabs that don't fit a phone's row (see trip-tabs.js).
    // Both the row buttons and these menu items exist at every width; which
    // set is visible is a CSS decision, so there's no resize listener and no
    // re-render on rotation. The menu is the same component as the locations
    // filter rather than a third hand-rolled popup - it just wears a static
    // label and tab styling.
    renderMenu(container.querySelector(".trip-tabs__more-slot"), {
      iconName: "ellipsis",
      label: t("trip.tabs.more"),
      chevron: false,
      triggerClass: OVERFLOW_TRIP_TABS.some(({ key }) => key === tab) ? "active" : "",
      className: "menu--tabs",
      ariaLabel: "trip.tabs.more",
      // Same icon each section shows in the row, so a tab that moved into the
      // menu still looks like itself.
      items: OVERFLOW_TRIP_TABS.map(({ key, icon: tabIcon }) => ({ value: key, label: t(`trip.tabs.${key}`), iconName: tabIcon })),
      // No neutralValue: unlike a filter, one of these is always the current
      // section or none of them is, and the trigger's own `active` class
      // already carries that.
      activeValue: tab,
      onSelect: (key) => {
        tab = key;
        window.history.pushState({}, "", `/trips/${trip.id}/${tab}`);
        render();
      },
    });

    const content = container.querySelector(".trip-tab-content");
    if (tab === "locations") {
      renderLocationsTab(content, trip, { signal: tabSignal });
    } else if (tab === "map") {
      // The map remembers where it was looking, but only in *this* history
      // entry. A camera change is a replaceState, so panning and zooming never
      // grows the back stack - going back from a location's page returns to
      // the same view, and going back once more leaves the map entirely. A tab
      // switch pushes a fresh (empty) state, so arriving at the map anew still
      // fits the trip's bounds.
      const savedView = window.history.state?.mapView;
      content.innerHTML = `<map-view trip-id="${trip.id}" locate fullscreen-toggle${
        savedView ? ` initial-view="${savedView.lng},${savedView.lat},${savedView.zoom}"` : ""
      }></map-view>`;
      content.addEventListener("map-view-change", (e) => {
        window.history.replaceState({ ...window.history.state, mapView: e.detail }, "");
      });
      // A marker popup's in-app link. map-view.js can't let the router's
      // [data-link] interception handle it - that listener sits on document and
      // a click inside a shadow root retargets to the host - so it dispatches
      // the same "location-open" event location-card.js does, and the page
      // turns it into a navigation exactly as locations-tab.js does.
      content.addEventListener("location-open", (e) => {
        navigate(`/trips/${trip.id}/locations/${e.detail.locationId}`);
      });
    } else if (tab === "itinerary") {
      renderItineraryTab(content, trip);
    } else if (tab === "notes") {
      // The tab owns both modes, so a viewer is not a separate render path
      // here the way it is for files and checklists — it simply never gets
      // the Edit button. notes-tab.js asks canEdit() itself.
      renderNotesTab(content, trip, { signal: tabSignal });
    } else if (tab === "files") {
      // file-list.js has had a documented read-only mode since Stage 11 (used
      // by the location view); a viewer is simply the second caller for it.
      renderFileList(content, `/trips/${trip.id}/files`, { readOnly: !canEdit(trip), shared: isShared(trip) });
    } else if (tab === "checklists") {
      renderChecklistList(content, trip.id, { readOnly: !canEdit(trip), shared: isShared(trip) });
    } else if (tab === "expenses") {
      // `shared` here does not mean visibility, as it does for files and
      // checklists — every expense is visible to everyone on the trip. It means
      // "is who paid a real question", which on a solo trip it is not.
      renderExpensesTab(content, trip, { readOnly: !canEdit(trip), shared: isShared(trip) });
    } else if (tab === "members") {
      renderMembersTab(content, trip, {
        // Keeps `trip.member_count` current for the other tabs. Mutated in
        // place rather than triggering a re-render: nothing visible on this
        // tab depends on the count, and the tabs that do read it when they
        // render, which is on the next tab switch.
        onMembersChanged: (count) => {
          trip.member_count = count;
        },
        pageSignal: signal,
      });
    } else if (tab === "settings") {
      renderSettingsTab(content, trip, {
        // Editing the trip and deleting it split here: an editor may rename a
        // trip or change its cover photo, only the owner may delete it.
        canEdit: canEdit(trip),
        canDelete: canManageMembers(trip),
        onTripUpdated: (updated) => {
          Object.assign(trip, updated);
          render();
        },
        pageSignal: signal,
      });
    }
  }

  render();
}
